import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { useAuth0 } from '@auth0/auth0-react';

import { setAuthTokenGetter, setLogoutHandler } from '@/store/api/baseApi';
import { usePatchUserLastLoginMutation } from '@/store/api/userApi';

import { useToken } from '@/hooks/useToken';

import { ErrorPage } from '../common/ErrorPage';
import { AuthLoading } from './AuthLoading';
import { LocalAccountPicker } from './LocalAccountPicker';
import { LocalAccountSwitcher } from './LocalAccountSwitcher';
import { Login } from './Login';
import {
  getLocalRole,
  getLocalToken,
  isLocalAuth,
  signOutLocal,
} from './localAuth';

// E2E mock auth key - must match global-setup.ts
const E2E_AUTH_KEY = 'e2e-auth-state';
const isMockAuth = import.meta.env.VITE_MOCK_AUTH === 'true';

/**
 * Check if E2E mock auth state exists in localStorage.
 * Used when VITE_MOCK_AUTH=true to bypass Auth0 SDK validation.
 */
function getE2EMockAuthState(): {
  isAuthenticated: boolean;
  accessToken: string | null;
} {
  if (!isMockAuth) {
    return { isAuthenticated: false, accessToken: null };
  }

  try {
    const stored = localStorage.getItem(E2E_AUTH_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.access_token) {
        return { isAuthenticated: true, accessToken: parsed.access_token };
      }
    }
  } catch (e) {
    console.error('[AuthGuard] Failed to parse E2E auth state:', e);
  }
  return { isAuthenticated: false, accessToken: null };
}

/**
 * Records the sign-in on the backend. Rendered only below the wrappers that install the token
 * getter, so the request always carries a token; the ref keeps a change in the trigger's identity
 * from recording the same sign-in twice.
 */
const LastLoginRecorder = () => {
  const [patchUserLastLogin] = usePatchUserLastLoginMutation();
  const recordedRef = useRef(false);

  useEffect(() => {
    if (recordedRef.current) return;
    recordedRef.current = true;
    patchUserLastLogin();
  }, [patchUserLastLogin]);

  return null;
};

const WithToken: React.FC<React.PropsWithChildren> = ({ children }) => {
  // Injects token to api
  const { accessToken, isTokenLoading } = useToken();

  if (isTokenLoading) return <AuthLoading />;
  // No token could be had for a reason that may pass; the page's Reload Page button asks again
  if (!accessToken) return <ErrorPage />;

  return <>{children}</>;
};

/**
 * WithToken variant for E2E mock auth - skips Auth0 token fetching.
 * Sets up the token getter for RTK Query's baseApi before App makes its first API call.
 */
const WithMockToken: React.FC<
  React.PropsWithChildren<{ accessToken: string }>
> = ({ children, accessToken }) => {
  // Set up the token getter before App makes its first API call. A layout effect runs before every
  // passive effect in the same commit, children's included, and RTK Query starts its requests in
  // passive effects.
  useLayoutEffect(() => {
    sessionStorage.setItem('accessToken-dev', accessToken);
    setAuthTokenGetter(async () => accessToken);
  }, [accessToken]);

  return <>{children}</>;
};

/**
 * WithToken variant for local mock sign-in (`npm run app`): the dev server signs a token for the
 * tab's local account. The getter asks again near expiry, so a long-lived tab keeps working.
 */
const WithLocalToken: React.FC<React.PropsWithChildren<{ role: string }>> = ({
  children,
  role,
}) => {
  // Set up the token getter before App makes its first API call, as WithMockToken does.
  // A 401 that survives the retry means the backend no longer trusts the key; back to the picker
  // rather than leaving every query silently empty.
  useLayoutEffect(() => {
    setAuthTokenGetter(() => getLocalToken(role));
    setLogoutHandler(signOutLocal);
  }, [role]);

  return (
    <>
      {children}
      <LocalAccountSwitcher role={role} />
    </>
  );
};

export const AuthGuard: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { isAuthenticated, isLoading, error } = useAuth0();

  // Check for E2E mock auth state
  const mockAuthState = useMemo(() => getE2EMockAuthState(), []);
  // Local mock sign-in: the account this tab chose, if any
  const localRole = useMemo(() => (isLocalAuth ? getLocalRole() : null), []);

  // Use mock auth if available, otherwise use Auth0
  const effectiveIsAuthenticated =
    mockAuthState.isAuthenticated || Boolean(localRole) || isAuthenticated;
  const effectiveIsLoading = isMockAuth ? false : isLoading;

  // A sign-in Auth0 refused returns to the sign-in screen with no toast; the reason goes only to
  // the console, once per error rather than on every render
  useEffect(() => {
    if (error) console.error('Authentication error', error);
  }, [error]);

  const signedIn = (
    <>
      <LastLoginRecorder />
      {children}
    </>
  );

  // Local mock sign-in replaces Auth0 entirely, so its states come before Auth0's
  if (isLocalAuth) {
    return localRole ? (
      <WithLocalToken role={localRole}>{signedIn}</WithLocalToken>
    ) : (
      <LocalAccountPicker />
    );
  }

  if (effectiveIsLoading) {
    return <AuthLoading />;
  }

  if (error && !mockAuthState.isAuthenticated) {
    return <Login />;
  }

  if (!effectiveIsAuthenticated) {
    return <Login />;
  }

  // If using mock auth, use the simpler WithMockToken component
  if (mockAuthState.isAuthenticated && mockAuthState.accessToken) {
    return (
      <WithMockToken accessToken={mockAuthState.accessToken}>
        {signedIn}
      </WithMockToken>
    );
  }

  return <WithToken>{signedIn}</WithToken>;
};
